import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useBrandAuthConfigOptional } from "@nextday/brand-auth";
import { useEffect, useRef } from "react";

/**
 * Handles the `?sso=CONNECTION_ID&org_code=ORG_CODE` redirect from Kinde
 * custom auth pages. The custom SSO buttons redirect the user back to the
 * client app origin with these params; we detect them here, clean the URL,
 * and trigger the Kinde SDK's `register()` with the connection so PKCE is
 * handled correctly. We use `register()` rather than `login()` because
 * the user arrives here from the registration funnel — `register()` will
 * create the account for new users and still sign in existing ones.
 *
 * Once Kinde finishes, it redirects back with `?code=&state=` which is
 * handled by `AuthCallbackPage` as usual.
 */

export const isSsoRedirectUrl = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return !!params.get("sso");
};

export const parseSsoParams = (
  search: string,
): { connectionId: string; orgCode: string | null } | null => {
  const params = new URLSearchParams(search);
  const connectionId = params.get("sso");
  if (!connectionId) return null;
  return { connectionId, orgCode: params.get("org_code") };
};

export const SsoRedirectPage = () => {
  const { register, isLoading } = useKindeAuth();
  const config = useBrandAuthConfigOptional();
  const triggered = useRef(false);

  useEffect(() => {
    if (isLoading || triggered.current) return;
    const sso = parseSsoParams(window.location.search);
    if (!sso) return;
    triggered.current = true;
    // Clean the URL so we don't loop on reload
    window.history.replaceState({}, "", window.location.pathname);
    const orgCode = sso.orgCode || config?.kindeOrgCode;
    void register({
      connectionId: sso.connectionId,
      ...(orgCode ? { orgCode } : {}),
    });
  }, [isLoading, register, config?.kindeOrgCode]);

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-[hsl(var(--brand-bg))] text-[hsl(var(--brand-bg-foreground))]">
      <div className="flex flex-col items-center gap-3">
        <div className="size-10 animate-spin rounded-full border-2 border-[hsl(var(--brand-bg-foreground)/0.2)] border-t-[hsl(var(--brand-bg-foreground))]" />
        <p className="text-sm opacity-80">Redirecting to sign in&hellip;</p>
      </div>
    </div>
  );
};

export default SsoRedirectPage;
